import React, { AnchorHTMLAttributes, FC, DetailedHTMLProps } from "react";

import { event as performTrackEvent, EventArgs } from "react-ga";

const TARGET_BLANK = "_blank";
const MIDDLE_CLICK = 1;

type LinkProps = DetailedHTMLProps<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

type Props = Omit<LinkProps, "onClick"> & {
  href: string;
  target: "_blank" | "_self" | "_parent" | "_top" | string;
  onClick?: (event: MouseEvent) => void;
  trackingData: {
    /**
     * A top level category for these events.
     * E.g. 'User', 'Navigation', 'App Editing', etc.
     */
    // @ts-ignore
    category: TRACKING_CATEGORY;
    /**
     * A description of the behaviour.
     * E.g. 'Clicked Delete', 'Added a component', 'Deleted account', etc.
     */
    action: string;
    /**
     * More precise labelling of the related action.
     * E.g. alongside the 'Added a component' action, we could add the name of a component as the label.
     * E.g. 'Survey', 'Heading', 'Button', etc.
     */
    label?: string;
    /**
     * A means of recording a numerical value against an event.
     * E.g. a rating, a score, etc.
     */
    value?: string | number;
  };
};

const isNormalClick = (event: MouseEvent) => {
  return !(
    event.ctrlKey ||
    event.shiftKey ||
    event.metaKey ||
    event.button === MIDDLE_CLICK
  );
};

export const TrackingLinkContainer: FC<Props> = (props) => {
  const { target, onClick, trackingData } = props;

  const isTargetingTheSamePage = target !== TARGET_BLANK;

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();

    performTrackEvent({ ...(trackingData as EventArgs) });

    onClick?.(event);

    if (isTargetingTheSamePage && isNormalClick(event)) {
      window.location.href = props.href;
    } else {
      window.open(props.href, "_black");
    }
  };

  const preparedHTMLLinkProps = {
    ...props,
    onClick: handleClick,
  };

  if (target === TARGET_BLANK) {
    preparedHTMLLinkProps.rel = `${
      props.rel ? props.rel : ""
    } noopener noreferrer`.trim();
  }

  // @ts-ignore
  delete preparedHTMLLinkProps.trackingData;

  return React.createElement("a", preparedHTMLLinkProps);
};
